import { CancelToken } from 'axios';
import {
  ConflictAction,
  ContactStatusType,
  SequenceAction,
  SequenceProgress,
  SequenceSortBy,
  SequenceStepExecutionType,
  SequenceStepPriorityValue,
  SequenceStepType,
} from '../enums';
import { Order } from '../../../shared/enums/order';
import { Status } from '../../../shared/enums/status';
import { SequenceSetting } from './sequence-setting';
import { MapConfig } from './contact';
import { UserSetting } from '../../../shared/types/user-setting';
import { EmailWritingFactors } from '../../../shared/functions/get-email-writing-factors';
import { EmailAccountType } from '../../email-accounts/enums/email-account';

// Sequence

export type CreateSequenceRequestPayload = {
  title: string;
};

export type RenameSequenceRequestPayload = {
  title: string;
};

export type PauseResumeSequenceRequestPayload = {
  action: SequenceAction;
  shouldPauseSubSequences?: boolean;
};

export type DeleteSequencesRequestPayload = {
  sequenceIds: number[];
};

export type GetSequencesQueryParams = {
  progress?: SequenceProgress;
  search?: string;
  sortBy?: SequenceSortBy;
  order?: Order;
  page?: number;
  limit?: number;
  owners?: string;
  clients?: string;
};

// Step
export type Variant = {
  payload: {
    subject?: string;
    preheader?: string;
    content?: string;
    message?: string;
    connectionNote?: string;
    purpose?: number;
    title?: string;
    action?: {
      name?: string;
      url?: string;
    };
  };
  templateId?: number;
  attachmentIds?: number[];
  taskNote?: string;
};
export type LinkedInStepConfig = {
  linkedinIntegrationId: number;
  externalCampaignId: string;
  heyreachLinkedinAccountId?: string | null;
};

export type AddSequenceStepRequestPayload = {
  executionType?: SequenceStepExecutionType;
  type?: SequenceStepType;
  absoluteDays: number;
  assigneeId?: number;
  priority?: SequenceStepPriorityValue;
  variants: Variant[];
  linkedInConfig?: LinkedInStepConfig;
};

export type UpdateLinkedInStepConfig = {
  externalCampaignId?: string;
  heyreachLinkedinAccountId?: string | null;
};

// Generic request's payload type.
export type UpdateSequenceStepRequestPayload = {
  subject?: string;
  content?: string;
  absoluteDays?: number;
  index?: number;
  templateId?: number;
  linkedInConfig?: UpdateLinkedInStepConfig;
};

export type UpdateSequenceStepExecutionDayRequestPayload = Pick<
  UpdateSequenceStepRequestPayload,
  'absoluteDays' | 'index'
>;

// Variant

export type AddSequenceStepVariantRequestPayload = {
  templateId?: number;
  attachmentIds?: number[];
  taskNote?: string;
  status?: number;
  payload: {
    subject?: string;
    preheader?: string;
    content?: string;
    message?: string;
    connectionNote?: string;
    purpose?: number;
    action?: {
      name?: string;
      url?: string;
    };
  };
  type?: SequenceStepType;
  priority?: SequenceStepPriorityValue;
  absoluteDays?: number;
  assigneeId?: number;
  taskAction?: number;
};

// Generic request's payload type.
export type UpdateSequenceStepVariantRequestPayload = {
  type?: SequenceStepType;
  templateId?: number;
  attachmentIds?: number[];
  absoluteDays?: number;
  priority?: SequenceStepPriorityValue;
  taskNote?: string;
  status?: Status;
  index?: number;
  assigneeId?: number;
  linkedInConfig?: UpdateLinkedInStepConfig;
  payload?: {
    subject?: string;
    preheader?: string;
    content?: string;
    message?: string;
    connectionNote?: string;
    purpose?: number;
    action?: {
      name?: string;
      url?: string;
    };
  };
};

const SequenceStepVariantRequestPayloadKeys = [
  'payload',
  'templateId',
  'absoluteDays',
  'index',
  'attachmentIds',
  'type',
  'taskNote',
  'priority',
  'assigneeId',
  'linkedInConfig',
] as const;

export type UpdateSequenceStepVariantDataRequestPayload = Pick<
  UpdateSequenceStepVariantRequestPayload,
  typeof SequenceStepVariantRequestPayloadKeys[number]
>;

export type UpdateSequenceStepVariantStatusRequestPayload = Pick<
  UpdateSequenceStepVariantRequestPayload,
  'status'
>;

// Settings

export type UpdateSequenceSettingsRequestPayload = {
  settings?: SequenceSetting[];
  scheduleId?: number;
  emailId?: number;
};

export type SaveProspectDealValuePayload = {
  prspectId?: string;
  dealValue?: number;
};

export type AddContactsFromCSVRequestPayload = {
  file: File;
  mapConfig: MapConfig;
  conflictAction: ConflictAction;
  includeRiskyContact?: boolean;
  includeVerification?: boolean;
  smartEmailVerification?: boolean;
  tags?: AddTagsToImportedContactsPayload;
  stepId: number;
  totalRecords?: number;
};

export type ChangeSequenceContactStatusPayload = {
  status: ContactStatusType;
  contactAndStepIds: any[];
  pauseDelayInDays?: number;
};

export type AddToStepParams = {
  stepId?: number;
  sequenceId: string;
  contactIds: number[];
};

export type SendTestEmailPayload = {
  fromEmailAccountId: number;
  subject: string;
  content: string;
  to: string[];
  stepNumber: number;
  sequenceId: number;
};

export type ImageUploadPayloadWithProgressCallback = {
  formData: FormData;
  onUploadProgress: any;
};

export type AttachmentUploadPayload = {
  formData: FormData;
  onUploadProgress: any;
  cancelToken: any;
};

export type AttachmentDeletePayload = {
  attachmentId: number;
  deleteFromTemplate: boolean;
  sequenceStepVariantId?: number;
  /** Distinguishes which UI initiated delete (shared global request state). */
  clientSource?: string;
};

/** `AttachmentDeletePayload.clientSource` — prospect modal notes tab / list. */
export const DELETE_ATTACHMENT_CLIENT_SOURCE_PROSPECT_NOTES = 'prospect-notes' as const;
/** `AttachmentDeletePayload.clientSource` — dialer call log note editor. */
export const DELETE_ATTACHMENT_CLIENT_SOURCE_CALL_LOGS_NOTE = 'call-logs-note' as const;

export type AttachmentUploadResponse = {
  attachmentId: number;
  attachmentIdentifier: string;
};

export type AttachmentDownloadPayload = {
  attachmentId: string;
};

export type UpdateUserSettingsRequestPayload = {
  settings?: UserSetting[];
};

export type GetSequenceProspectActivityPayload = {
  sequenceId: string;
  prospectId: string;
};

export type AddTagsToImportedContactsPayload = {
  ids: number[];
  newTags: string[];
};

export type GetSequenceScorePayload = {
  sequenceId: string;
  refresh?: boolean;
};

export type EmailWritingFactorsPayload = {
  sequenceId: number;
  stepId: number;
  emailWritingFactors: EmailWritingFactors[];
};

export type LinkedEmailAccount = {
  senderName: string;
  senderEmail: string;
  activeSequence: number;
  totalDailyLimit: number;
  usedDailyLimit: number;
  emailHealthScore: number;
  owner: string;
  isAnySequenceMailSent: boolean;
  status: number;
  emailType: EmailAccountType;
  isSignaturePresent: boolean;
  hashId: string;
  tags?: {
    id?: number;
    name?: string;
  }[];
};

export type GetLinkedEmailAccountsResponse = {
  responseWithActiveSequences: LinkedEmailAccount[];
  hasMore: boolean;
};

export type GetAllNotLinkedEmailAccountsResponse = {
  hasMore: boolean;
  responseWithActiveSequences: {
    activeSequence: string;
    availableQuota: string;
    id: number;
    emailType: number;
    status: number;
    owner: string;
    senderEmail: string;
    senderName: string;
    totalDailyLimit: string;
  }[];
};

export type GetSenderEmailsResponse = {
  id: number;
  fromEmail: string;
  fromName: string;
};

export type GetSequenceTimezonePayload = {
  sequenceId: number;
  uniqueId: number; // This is used for multi editor instance
};

export type SequenceProspectBulkStatusUpdatePayload = {
  sequenceId: string;
  status: ContactStatusType;
  pauseDelayInDays?: number;
  deSelectedContactIds?: number[];
  contactsFilter?: Record<string, any>;
};

export type SequenceProspectBulkTagAssignPayload = {
  sequenceId: string;
  ids?: number[];
  newTags: string[];
  deSelectedContactIds?: number[];
  contactsFilter?: Record<string, any>;
};

export type SequenceProspectBulkTagUnAssignPayload = {
  sequenceId: string;
  tagIds?: number[];
  deSelectedContactIds?: number[];
  contactsFilter?: Record<string, any>;
};

export type SequenceProspectBulkAddToSequencePayload = {
  sequenceId: string;
  stepId: number[];
  deSelectedContactIds?: number[];
  contactsFilter: Record<string, any>;
};

export type SequenceProspectBulkRemoveFromSequencePayload = {
  sequenceId: string;
  deSelectedContactIds?: number[];
  contactsFilter: Record<string, any>;
};

export type GetAIGeneratedEmailVariantPayload = {
  sequenceId: number;
  stepId: number;
  variantId: number;
  cancelToken?: CancelToken;
};
